import React, { useState, useEffect } from "react";
import {
  IconButton,
  Drawer,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  CircularProgress as MuiCircularProgress,
  alpha,
  List,
  Divider,
} from "@mui/material";
import Avatar from "components/@extended/Avatar";
import { X as CloseIcon, Heart } from "lucide-react";
import {
  Profile2User,
  Book1,
  Notification,
  Setting2,
  MessageQuestion,
  Profile,
  LogoutCurve,
  Award,
} from "iconsax-react";
import { useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel } from "utils/adminAccess";
import axios from "utils/axios";
import icons from "assets/icons";
import { MailPlus, Layout } from "lucide-react";
import { useGetUser } from "api/user";

const MobileHeaderMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const { user: userData } = useGetUser();
  const navigate = useNavigate();
  const isAdmin = canAccessAdminPanel(user);

  const open = Boolean(anchorEl);

  useEffect(() => {
    fetchGlobalPoints();
  }, []);

  const fetchGlobalPoints = async () => {
    try {
      const response = await axios.get("/api/gamification/dashboard-data");
      if (response.data?.global) {
        setGlobalData(response.data.global);
      }
    } catch (err) {
      console.error("Error fetching global points:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateTo = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error(err);
    }
  };

  function stringAvatar(firstName, lastName) {
    if (!firstName || !lastName)
      return {
        sx: {
          bgcolor: "primary.100",
          fontWeight: 700,
          fontSize: 12,
        },
      };

    return {
      sx: {
        bgcolor: "primary.100",
        fontWeight: 700,
        fontSize: 12,
      },
      children: `${firstName[0]}${lastName[0]}`.toUpperCase(),
    };
  }

  // Quick nav items
  const standardNavCards = [
    {
      title: "Experts",
      icon: icons.expert,
      path: "/experts",
    },
    {
      title: "Institutions",
      icon: icons.institutions,
      path: "/institutions",
    },
    {
      title: "Newsletters",
      icon: icons.news,
      path: "/newsletter",
    },
    {
      title: "Formations",
      icon: icons.formation,
      path: "/formation",
    },
  ];

  const adminNavCards = [
    {
      title: "Abonné",
      IconComponent: MailPlus,
      path: "/admin/newsletters",
      color: "#4f46e5",
    },
    {
      title: "Bannières publicitaires",
      IconComponent: Layout,
      path: "/admin/list-banners",
      color: "#f97316",
    },
  ];

  // Dashboard items matching ProfileTab
  const dashboardItems = [
    { title: "Mon profil", icon: Profile2User, path: "/profile/account/basic" },
    { title: "Mon apprentissage", icon: Book1, path: "/apprentissage" },
    { title: "Mes Certificats", icon: Award, path: "/certificats" },
    { title: "Mes favoris", icon: Heart, path: "/favorites" },
    { title: "Mes newsletters", icon: Notification, path: "/newsletter" },
  ];

  const { points, progress } = globalData || {};
  const progressPercentage = progress?.progress_percentage || 0;

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          p: 0,
        }}
      >
        <Avatar
          {...stringAvatar(userData?.first_name, userData?.last_name)}
          src={userData?.profile_image_url}
          sx={{ width: 35, height: 35, fontSize: 14 }}
        />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
            bgcolor: "#f8f9fa",
          },
        }}
      >
        {/* Header with Close Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            bgcolor: "white",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon size={24} />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: "auto", height: "100%", bgcolor: "#f8f9fa" }}>
          {/* User Info */}
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: "white",
              mb: 1,
            }}
          >
            <Avatar
              {...stringAvatar(userData?.first_name, userData?.last_name)}
              src={userData?.profile_image_url}
              sx={{
                width: 56,
                height: 56,
                bgcolor: "primary.main",
              }}
            />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {userData?.first_name} {userData?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userData?.email}
              </Typography>
            </Box>
          </Box>

          {/* Mon Tableau de Bord Section */}
          <Box sx={{ bgcolor: "white", mb: 1, p: 2 }}>
            <Typography variant="h6" sx={{ pb: 1, fontWeight: 600 }}>
              Mon Tableau de Bord
            </Typography>
            <Divider />

            <List component="div" disablePadding sx={{ mt: 1 }}>
              {dashboardItems.map((item) => {
                const Icon = item.icon;
                return (
                  <MenuItem
                    key={item.title}
                    onClick={() => navigateTo(item.path)}
                    sx={{ py: 1.5, px: 0 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={item.title} />
                  </MenuItem>
                );
              })}
            </List>
          </Box>

          {/* Parameters & Support */}
          <Box sx={{ bgcolor: "white", mb: 1, p: 2 }}>
            <Divider sx={{ mb: 1 }} />

            <MenuItem
              onClick={() => navigateTo("/profile/setting/interests")}
              sx={{ py: 1.5, px: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Setting2 size={20} />
              </ListItemIcon>
              <ListItemText primary="Paramètres" />
            </MenuItem>

            <MenuItem
              onClick={() => {
                // Redirect to assistance page on landing
                const landingUrl = import.meta.env.VITE_LANDING_URL || `${window.location.protocol}//${window.location.hostname}`;
                const locale = localStorage.getItem('i18nextLng') || 'fr';
                window.location.href = `${landingUrl}/${locale}/assistance`;
              }}
              sx={{ py: 1.5, px: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MessageQuestion size={20} />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </MenuItem>

            {/* Admin Link */}
            {isAdmin && (
              <MenuItem
                onClick={() => navigateTo("/admin/dashboard")}
                sx={{ py: 1.5, px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Profile size={20} />
                </ListItemIcon>
                <ListItemText primary="Admin" />
              </MenuItem>
            )}
          </Box>

          {/* Quick Nav Items */}
          <Box sx={{ bgcolor: "white", p: 2, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Accès rapide
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1.5,
              }}
            >
              {standardNavCards.map((card) => (
                <Box
                  key={card.title}
                  onClick={() => navigateTo(card.path)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#F0F4FF",
                    border: "1px solid #e3f2fd",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: alpha("#0049e0", 0.1),
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={card.icon}
                    alt={card.title}
                    sx={{
                      width: 32,
                      height: 32,
                      mb: 1,
                      filter: "brightness(0)",
                    }}
                  />
                  <Typography variant="body2" align="center" fontWeight={500}>
                    {card.title}
                  </Typography>
                </Box>
              ))}

              {/* Admin Quick Nav */}
              {isAdmin &&
                adminNavCards.map((card) => (
                  <Box
                    key={card.title}
                    onClick={() => navigateTo(card.path)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "#F0F4FF",
                      border: "1px solid #e3f2fd",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: alpha("#0049e0", 0.1),
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <card.IconComponent
                      size={32}
                      color={card.color}
                      strokeWidth={2}
                    />
                    <Typography
                      variant="body2"
                      align="center"
                      fontWeight={500}
                      sx={{ mt: 1 }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>

          {/* Global Points Display */}
          {!loading && globalData && (
            <Box sx={{ bgcolor: "white", mb: 1 }}>
              <MenuItem
                onClick={() => navigateTo("/profile/account/dashboard")}
                sx={{ py: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <MuiCircularProgress
                      variant="determinate"
                      value={100}
                      size={60}
                      thickness={4}
                      sx={{
                        color: "#e3f2fd",
                        position: "absolute",
                        left: 0,
                        top: 0,
                      }}
                    />
                    <MuiCircularProgress
                      variant="determinate"
                      value={progressPercentage}
                      size={60}
                      thickness={4}
                      sx={{
                        color: "#0049e0",
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        {points}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Points Globaux
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {progressPercentage}% progression
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Box>
          )}

          {/* Logout */}
          <Box sx={{ bgcolor: "white", mb: 1, p: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutCurve size={20} />
              </ListItemIcon>
              <ListItemText primary="Se déconnecter" />
            </MenuItem>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileHeaderMenu;
